import { Suspense, useState } from 'react';

import { Constants } from '@constants';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { MicroLoanOfferBanner } from './MicroLoanOfferBanner';
import { useGetMicroLoanOfferViewData } from './useGetMicroLoanOfferViewData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import { RecommendedOfferContentUI } from '../recommendedOfferView/RecommendedOfferContent';
import { RecommendedOfferDetails } from '../recommendedOfferView/RecommendedOfferDetails';

export const MicroLoanOfferView = ({
    formalOfferId
}: {
    formalOfferId: TFormalOffer['offer_id'];
}) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <div
                        className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                        data-testId="loan-overview-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <MicroLoanOfferContent offerId={formalOfferId!} />
            </Suspense>
        </ErrorBoundary>
    );
};

const MicroLoanOfferContent = ({
    offerId
}: {
    offerId: NonNullable<TFormalOffer['offer_id']>;
}) => {
    const { formalOffer, loanDuration } = useGetMicroLoanOfferViewData(offerId);

    const [drawdownAmount, setDrawdownAmount] = useState<number>(
        formalOffer.available_cash || 0
    );

    return (
        <MainSectionWrapper className="w-ful min-h-screen">
            <div className="mb-4xl flex flex-col items-center justify-center gap-s">
                <h1 className="text-center max-sm:mb-xl max-sm:leading-10">
                    <FormattedMessage
                        id="_.recommended_offer_view.heading"
                        defaultMessage="You're approved for up to {amount}"
                        values={{
                            amount: (
                                <span className="text-success-drk">
                                    <FormattedCurrency
                                        value={formalOffer!.available_cash!}
                                    />
                                </span>
                            )
                        }}
                    />
                </h1>
                <div>
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.timeout"
                        defaultMessage="This offer is valid until {timeout}"
                        values={{
                            timeout: (
                                <FormattedDate
                                    value={new Date(formalOffer?.timeout!)}
                                    day="2-digit"
                                    month="long"
                                    year="numeric"
                                />
                            )
                        }}
                    />
                </div>
            </div>

            <div className="flex items-center justify-center rounded-t-m border border-b-0 border-structure-95 bg-success-drk text-center text-structure-100 max-sm:mx-m max-sm:mb-[-5px] max-sm:flex-col">
                <MicroLoanOfferBanner offerId={offerId} />
            </div>

            <div className="flex w-full flex-row items-start justify-evenly rounded-b-m border border-structure-95 bg-structure-100 shadow-blue-s max-sm:m-auto max-sm:flex-col max-sm:border-none max-sm:bg-transparent max-sm:px-m max-sm:shadow-transparent">
                <RecommendedOfferContentUI
                    loanDuration={loanDuration!}
                    minimumDrawdownAmount={Constants.MIN_DRAW_DOWN_AMOUNT}
                    setDrawdownAmount={setDrawdownAmount}
                    drawdownAmount={drawdownAmount}
                    maximumDrawdownAmount={formalOffer?.available_cash!}
                    offerTimeout={formalOffer?.timeout!}
                    offerId={formalOffer?.offer_id!}
                    productType={formalOffer?.product_type!}
                    hideDisclaimer={true}
                />
                <RecommendedOfferDetails
                    drawdownAmount={drawdownAmount}
                    recommendedOffer={formalOffer!}
                    loanDuration={loanDuration!}
                    hideEarlyRepaymentLink={true}
                />
            </div>
        </MainSectionWrapper>
    );
};
